import { resizeScroll } from '../core/ScrollService.js';

function getMinHeight(container) {
    return parseInt(getComputedStyle(container).getPropertyValue('--embed-min-height'), 10) || 0;
}

function findIframes(root) {
    if (!root) {
        return [];
    }

    const iframes = [];

    if (root.tagName === 'IFRAME') {
        iframes.push(root);
    }

    root.querySelectorAll?.('iframe').forEach((iframe) => {
        iframes.push(iframe);
    });

    root.querySelectorAll?.('*').forEach((element) => {
        if (element.shadowRoot) {
            iframes.push(...findIframes(element.shadowRoot));
        }
    });

    return iframes;
}

function findFragments(container) {
    const fragments = new Set();

    container.querySelectorAll('[data-external-form]').forEach((element) => {
        fragments.add(element);
    });

    container.querySelectorAll('*').forEach((element) => {
        if (element.localName?.startsWith('fragment-')) {
            fragments.add(element);
        }
    });

    return [...fragments];
}

function measureNodeHeight(node) {
    if (!node) {
        return 0;
    }

    const rect = node.getBoundingClientRect();

    return Math.max(rect.height, node.offsetHeight, node.scrollHeight);
}

function updateContainerHeight(container) {
    const minHeight = getMinHeight(container);
    let height = minHeight;

    findFragments(container).forEach((fragment) => {
        height = Math.max(height, measureNodeHeight(fragment));
        findIframes(fragment).forEach((iframe) => {
            height = Math.max(height, measureNodeHeight(iframe));
        });
    });

    findIframes(container).forEach((iframe) => {
        height = Math.max(height, measureNodeHeight(iframe));
    });

    height = Math.max(height, measureNodeHeight(container));

    if (height > minHeight) {
        container.style.minHeight = `${height}px`;
    }
}

function bindEmbedObservers(container) {
    const observed = new WeakSet();

    const observeNode = (node) => {
        if (!node || observed.has(node)) {
            return;
        }

        observed.add(node);
        resizeObserver.observe(node);
    };

    const scan = () => {
        observeNode(container);
        findFragments(container).forEach(observeNode);
        findIframes(container).forEach(observeNode);
        updateContainerHeight(container);
    };

    const resizeObserver = new ResizeObserver(() => {
        updateContainerHeight(container);
        resizeScroll();
    });

    if (typeof ResizeObserver === 'undefined') {
        scan();
        return scan;
    }

    const mutationObserver = new MutationObserver(scan);

    mutationObserver.observe(container, {
        childList: true,
        subtree: true,
    });

    scan();

    [500, 1500, 3000].forEach((delay) => {
        setTimeout(scan, delay);
    });

    return scan;
}

function bindPostMessageResize() {
    window.addEventListener('message', (event) => {
        const data = event.data;

        if (!data || typeof data !== 'object') {
            return;
        }

        const isResizeEvent =
            data.type === 'hsFormCallback' ||
            data.type === 'resize' ||
            data.eventName === 'resize' ||
            typeof data.height === 'number' ||
            typeof data.frameHeight === 'number';

        if (!isResizeEvent) {
            return;
        }

        const height = data.height || data.frameHeight;

        document.querySelectorAll('.js-external-embed').forEach((container) => {
            const iframes = findIframes(container);
            const matchingIframe = iframes.find((iframe) => iframe.contentWindow === event.source);

            if (matchingIframe && height) {
                matchingIframe.style.height = `${height}px`;

                const fragment = matchingIframe.closest('[data-external-form]');

                if (fragment && fragment !== container) {
                    fragment.style.minHeight = `${height}px`;
                }

                updateContainerHeight(container);
                resizeScroll();
                return;
            }

            if (data.type === 'hsFormCallback') {
                updateContainerHeight(container);
                resizeScroll();
            }
        });
    });
}

export function initExternalEmbed() {
    const containers = document.querySelectorAll('.js-external-embed');

    if (!containers.length) {
        return;
    }

    containers.forEach((container) => {
        bindEmbedObservers(container);
    });

    bindPostMessageResize();
}
